// Location: src/features/devices/components/OtpDialog.tsx

import { useState, useEffect } from 'react';
import {
  useTranslate,
  useNotify,
  useDataProvider,
  useRedirect,
  usePermissions,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy,
  VpnKey,
  Settings,
  CalendarToday,
} from '@mui/icons-material';
import type { Device, UserRole } from '@/types/models';
import { hasResourcePermission } from '@/config/resourceConfig';

interface OtpDialogProps {
  open: boolean;
  onClose: () => void;
  record: Pick<Device, 'id' | 'installationCode'>;
}

interface OtpData {
  serialNumber: string;
  operatorOtp: string;
  settingsOtp: string;
  validForDay: string;
}

const OtpDialog = ({ open, onClose, record }: OtpDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const { permissions } = usePermissions();
  const role = permissions as UserRole;
  const canSeeSettingsOtp = role === 'SuperAdmin' || role === 'Admin' || role === 'GroupAdmin';

  const [otp, setOtp] = useState<OtpData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noSerial, setNoSerial] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // yyyy-MM-dd
  });

  const fetchOtp = async (date?: string) => {
    setLoading(true);
    setError(null);
    setNoSerial(false);

    try {
      const dateParam = date
        ? date.replace(/-/g, '') // yyyy-MM-dd → yyyyMMdd
        : undefined;
      const result = await (dataProvider as any).generateDeviceOtp(record.id, dateParam);
      setOtp(result.data);
    } catch (err: any) {
      const code = err?.body?.error?.code;
      if (code === 'NO_SERIAL_NUMBER') {
        setNoSerial(true);
      } else {
        const msg =
          err?.body?.error?.message ||
          err?.message ||
          translate('resources.devices.messages.otpError');
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setOtp(null);
      setError(null);
      setNoSerial(false);
      fetchOtp(selectedDate);
    }
  }, [open]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchOtp(newDate);
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify('resources.devices.messages.otpCopied', { type: 'success' });
    } catch {
      // fallback
    }
  };

  const handleConfigureSerial = () => {
    onClose();
    redirect('edit', 'devices', record.id);
  };

  const formatDay = (day: string) => {
    if (day.length === 8) {
      return `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}`;
    }
    return day;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VpnKey fontSize="small" color="primary" />
        {translate('resources.devices.messages.otpTitle')}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {record.installationCode}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : noSerial ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('resources.devices.messages.otpNoSerialNumber')}
          </Alert>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : otp ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Serial Number */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                {translate('resources.devices.fields.serialNumber')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'action.hover',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  mt: 0.5,
                }}
              >
                {otp.serialNumber}
              </Typography>
            </Box>

            {/* Date Picker */}
            <TextField
              type="date"
              label={translate('resources.devices.messages.otpDate')}
              value={selectedDate}
              onChange={handleDateChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <CalendarToday fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />

            {/* Operator OTP */}
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {translate('resources.devices.messages.otpOperator')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    color: 'primary.main',
                  }}
                >
                  {otp.operatorOtp}
                </Typography>
              </Box>
              <Tooltip title={translate('resources.devices.actions.copyOtp')}>
                <IconButton onClick={() => handleCopy(otp.operatorOtp)} color="primary">
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Settings OTP (only for SuperAdmin and GroupAdmin) */}
            {canSeeSettingsOtp && (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {translate('resources.devices.messages.otpSettings')}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      letterSpacing: '0.15em',
                      color: 'secondary.main',
                    }}
                  >
                    {otp.settingsOtp}
                  </Typography>
                </Box>
                <Tooltip title={translate('resources.devices.actions.copyOtp')}>
                  <IconButton onClick={() => handleCopy(otp.settingsOtp)} color="secondary">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Valid for day */}
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {translate('resources.devices.messages.otpValidFor')}: {formatDay(otp.validForDay)}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        {noSerial && hasResourcePermission('devices', 'edit') ? (
          <>
            <Button onClick={onClose}>
              {translate('ra.action.cancel')}
            </Button>
            <Button
              onClick={handleConfigureSerial}
              variant="contained"
              color="primary"
            >
              {translate('resources.devices.messages.otpConfigureFirst')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained">
            {translate('ra.action.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OtpDialog;
